import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { sendOtpSms, verifyPhone } from "@/api";
import { handleIncompleteRegistration } from "@/lib/handleIncompleteRegistration";
import StepHeader from "@/components/StepHeader";
import ActionRow from "@/components/ActionRow";
import ErrorModal from "@/components/ErrorModal";
import { extractApiError } from "@/lib/extractApiError";

const PhoneVerifyPage = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  const registerData = sessionStorage.getItem("registerData");

  // Page guard
  useEffect(() => {
    if (!registerData) navigate("/register");
  }, [registerData, navigate]);

  // Timer countdown
  useEffect(() => {
    if (timer <= 0) {
      setCanResend(true);
      return;
    }
    const id = setTimeout(() => setTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);

  const formatTime = (s) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const getFormattedPhone = () => `+49${phone.replace(/^0/, "")}`;

  const buildSmsPayload = () => {
    const payload = {
      consumer: { mobilePhone: getFormattedPhone() },
      campaign: { ident: sessionStorage.getItem("campaignIdent") },
      otp: { domain: window.origin },
    };
    const promoterCode = sessionStorage.getItem("promoterCode");
    const handlerCode = sessionStorage.getItem("handlerCode");
    if (promoterCode) payload.promoter = { ident: promoterCode };
    if (handlerCode) payload.store = { ident: handlerCode };
    return payload;
  };

  const getToken = () => {
    const parsed = JSON.parse(registerData);
    return parsed.token.uuid;
  };

  const handleSendSms = async (e) => {
    e.preventDefault();
    if (!phone) return;
    setError(null);
    setIsLoading(true);
    try {
      await sendOtpSms(buildSmsPayload(), getToken());
      setOtpSent(true);
      setTimer(180);
      setCanResend(false);
    } catch (err) {
      setError(extractApiError(err.response?.data));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await sendOtpSms(buildSmsPayload(), getToken());
      setTimer(180);
      setCanResend(false);
    } catch {
      /* silent */
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!otpCode) return;
    setError(null);
    setIsLoading(true);
    try {
      await verifyPhone(
        { consumer: { mobilePhone: getFormattedPhone(), otpPhone: otpCode } },
        getToken()
      );
      // Success — phone verified and registration fully complete (rare)
      sessionStorage.removeItem("registerData");
    } catch (err) {
      const apiError = err.response?.data;
      if (apiError?.data?.error === "error_registration_incomplete") {
        handleIncompleteRegistration(apiError.data.details, navigate);
      } else {
        setError(extractApiError(apiError));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!registerData) return null;

  // Phone number entry sub-screen
  if (!otpSent) {
    return (
      <>
        <Button
          type="button"
          size="sm"
          className="absolute top-3 left-3 z-10 md:h-[54px] md:px-[20px] md:text-[24px] md:rounded-[14px]"
          onClick={() => { sessionStorage.removeItem("migration"); navigate("/"); }}
        >
          Anmelden
        </Button>

        <form
          onSubmit={handleSendSms}
          className="flex flex-col gap-10 items-center w-full max-w-[720px] pt-[80px] pb-[48px] px-5"
        >
          <StepHeader stepTitle="Gib deine Telefonnummer ein" step={3} migration={sessionStorage.getItem("migration") === "true"} />
          <div className="flex flex-col gap-6 items-center w-full max-w-[480px]">
            <div className="w-full">
              <label className="font-extrabold text-[22px] text-primary leading-[1.4] mb-1 block">
                Telefonnummer
              </label>
              <div className="flex items-center border-b border-[#ababab] focus-within:border-black transition-colors">
                <span className="font-extrabold text-[20px] text-black pl-2 py-2 select-none">
                  +49
                </span>
                <input
                  type="tel"
                  placeholder="XXXXXXXXX"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="flex h-[64px] w-full bg-transparent px-2 py-2 font-extrabold text-[20px] text-black placeholder:text-[#9e9e9e] focus-visible:outline-none"
                />
              </div>
            </div>
            <ErrorModal
              open={!!error}
              onClose={() => setError(null)}
              message={error?.message}
              errorCode={error?.errorCode}
            />
            <Button
              type="submit"
              className="w-full md:h-[54px] md:text-[24px] md:rounded-[14px]"
              size="sm"
              variant={phone ? "default" : "disabled"}
              disabled={!phone || isLoading}
            >
              {isLoading ? "Bitte warte..." : "Weiter"}
            </Button>
          </div>
        </form>
      </>
    );
  }

  // SMS OTP verification sub-screen
  return (
    <>
      <Button
        type="button"
        size="sm"
        className="absolute top-3 left-3 z-10 md:h-[54px] md:px-[20px] md:text-[24px] md:rounded-[14px]"
        onClick={() => { sessionStorage.removeItem("migration"); navigate("/"); }}
      >
        Anmelden
      </Button>

      <form
        onSubmit={handleVerify}
        className="flex flex-col gap-10 items-center w-full max-w-[720px] pt-[80px] pb-[48px] px-5"
      >
        <div className="flex flex-col gap-4 items-center w-full">
          <StepHeader stepTitle="SMS-Verifizierung" step={3} migration={sessionStorage.getItem("migration") === "true"} />
          <p className="text-black text-center text-[18px] font-medium leading-[1.4]">
            Wir haben einen Code an{" "}
            <span className="font-extrabold">{getFormattedPhone()}</span>{" "}
            gesendet. Gib ihn unten ein.
          </p>
        </div>
        <div className="flex flex-col gap-6 items-center w-full max-w-[480px]">
          <Input
            label="SMS-Code"
            placeholder="Code eingeben"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value)}
          />

          <div className="flex items-center gap-2.5 w-full">
            <div className="flex-1 text-[16px] text-black font-medium">
              <p className="leading-[1.4]">Hast du keinen Code?</p>
              {!canResend && (
                <p className="leading-[1.4]">
                  Bitte warte:{" "}
                  <span className="font-extrabold text-primary">
                    {formatTime(timer)}
                  </span>
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleResend}
              disabled={!canResend}
              className={`font-extrabold text-[18px] text-black uppercase text-right leading-[28px] min-h-[40px] ${!canResend ? "opacity-35 pointer-events-none" : "hover:underline cursor-pointer"}`}
            >
              Code erneut senden
            </button>
          </div>

          <ErrorModal
            open={!!error}
            onClose={() => setError(null)}
            message={error?.message}
            errorCode={error?.errorCode}
          />

          <ActionRow
            onBack={() => setOtpSent(false)}
            label="Weiter"
            disabled={!otpCode}
            isLoading={isLoading}
          />
        </div>
      </form>
    </>
  );
};

export default PhoneVerifyPage;
